import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '../ui';
import { Users } from 'lucide-react';
import { getLimitStatusColor, formatCurrency } from '../../lib/utils';

/**
 * AccountLimits Component
 * 
 * Displays account monthly limit tracking with visual progress bars.
 * Shows current usage, limits, and remaining amounts for each account.
 * Uses React.memo for performance optimization.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.accountLimits - Array of account limit objects
 * @returns {JSX.Element} Account limits card grid
 */
const AccountLimits = React.memo(function AccountLimits({ accountLimits }) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          აქაუნთების ლიმიტები (თვე)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {accountLimits?.map((account) => (
            <div
              key={account.id}
              className="p-4 border rounded-lg"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">{account.name}</span>
                <Badge
                  variant={account.limit_status === 'exceeded' ? 'destructive' : account.limit_status === 'warning' ? 'secondary' : 'default'}
                >
                  {account.limit_status === 'exceeded' ? 'ლიმიტი გადაჭარბებულია' :
                   account.limit_status === 'warning' ? 'ყურადღება' : 'ნორმა'}
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">გამოყენებული:</span>
                  <span className={getLimitStatusColor(account.limit_status)}>
                    {formatCurrency(account.current_month_total)} / {formatCurrency(account.monthly_limit)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      account.limit_status === 'exceeded' ? 'bg-red-500' :
                      account.limit_status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{
                      width: `${Math.min((account.current_month_total / account.monthly_limit) * 100, 100)}%`
                    }}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  დარჩენილი: {formatCurrency(account.remaining_limit)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});

// PropTypes for type checking
AccountLimits.propTypes = {
  accountLimits: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      limit_status: PropTypes.oneOf(['ok', 'warning', 'exceeded']),
      current_month_total: PropTypes.number,
      monthly_limit: PropTypes.number,
      remaining_limit: PropTypes.number
    })
  )
};

export default AccountLimits;
